<template>
  <div
    v-if="isOpen"
    class="location-wrapper options-group"
    :class="isFullScreen ? 'location-wrapper--mobile' : 'location-wrapper--desktop'"
    id="offers-location-modal"
    data-testid="modal-location-filter"
  >
    <div v-if="isFullScreen" class="location--header">
      <h2>Location</h2>
      <button
        class="btn"
        aria-label="Close"
        @click="emitToggle(false)"
        data-testid="close-modal-button"
      >
        <i class="icon-cross sm" />
      </button>
    </div>
    <div class="location--body">
      <div class="filters-section" v-if="activeFilter !== 'geolocation'">
        <input
          type="text"
          v-model="inputLocation"
          v-if="isFullScreen"
          class="input-location-mobile"
          :placeholder="translate('offDealsLocationPlaceholderLarge')"
        />
      </div>
      <template v-if="!inputLocation">
        <!--Nearby-->
        <div class="filters-section">
          <button class="btn btn-search-nearby" @click="getLocation" data-testid="nearby-button">
            <span>{{ translate('offDealsLocationSearchNearby') }}</span>
            <i class="icon-location xs" v-if="!isLoadingCoords" />
          </button>
        </div>
        <!--    Search results-->
        <div class="location-results">
          <h2 class="title">{{ translate('offDealsLookingForRegion') }}</h2>
          <div class="results">
            <button
              class="btn item"
              :class="{
                'item-selected': isSelected(region)
              }"
              v-for="region in regions"
              :key="region.code"
              @click="handleSelect(region)"
            >
              <span>{{ region.name }}</span>
              <i class="icon-check xs" v-if="isSelected(region)" />
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <ul class="location-results--search">
          <template v-if="filteredLocations.length">
            <li v-for="loc in filteredLocations" :key="loc.code">
              <button
                class="btn filtered-location-name"
                @click="handleSelect(loc)"
                :aria-label="loc.name"
              >
                <span class="highlighted-text">{{ loc.name.slice(0, inputLocation.length) }}</span>
                <span>{{ loc.name.slice(inputLocation.length) }}</span>
              </button>
            </li>
          </template>

          <template v-else>
            <li class="no-results-msg">
              <span>{{ translate('offDealsNoMatchingResults') }}</span>
            </li>
          </template>
        </ul>
      </template>
    </div>

    <div v-if="isFullScreen" class="location--cta">
      <button
        class="btn btn-secondary"
        aria-label="Reset location"
        @click="emitClear()"
        data-testid="reset-filters-button"
      >
        <i class="icon-close sm" />
        <span>{{ translate('offDealsClearFilters') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters, mapActions } = createNamespacedHelpers('newOffers')
export default {
  name: 'OffersLocationModal',
  emits: ['toggle', 'clear', 'apply', 'select'],
  props: {
    isFullScreen: {
      type: Boolean,
      default: false
    },
    location: {
      type: String,
      default: ''
    },
    regions: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    countries: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    cities: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    selectedRegion: {
      type: [String, Array],
      required: false,
      default: ''
    },
    currentFilterRegion: {
      type: [String, Array],
      required: true
    },
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentSelection: { code: '', name: '' },
      isLoadingCoords: false,
      position: null,
      selectedLocation: '',
      activeFilter: '',
      nearbyDistance: 300,
      inputLocation: this.location
    }
  },
  watch: {
    isOpen() {
      const bodyEl = document.querySelector('body')
      this.isOpened && this.isFullScreen
        ? bodyEl.classList.add('overflow-hidden')
        : bodyEl.classList.remove('overflow-hidden')
    },
    location(newValue) {
      this.inputLocation = newValue
      if (newValue === '') {
        this.currentSelection = { code: '', name: '' }
        this.selectedLocation = ''
        this.activeFilter = ''
      }
    },
    currentFilterRegion(newValue) {
      if (!newValue) {
        this.currentSelection = { code: '', name: '' }
      }
    }
  },
  computed: {
    ...mapGetters(['filter']),
    allLocations() {
      return [...this.countries, ...this.cities, ...this.regions]
    },
    filteredLocations() {
      const cleanLocationInput = this.inputLocation.trim().toLowerCase()

      return this.allLocations.filter(({ name }) => {
        return !!name.toLowerCase().startsWith(cleanLocationInput)
      })
    }
  },
  methods: {
    ...mapActions(['selectLocation']),
    emitToggle(value) {
      this.inputLocation = this.location
      this.$emit('toggle', value)
    },
    emitClear() {
      this.$emit('clear')
    },
    emitApply() {
      this.$emit('apply', {
        [this.activeFilter || 'region']: this.currentSelection,
        position: this.position,
        location: this.selectedLocation,
        activeFilter: this.activeFilter
      })
      this.$emit('inputLocation', this.inputLocation)
    },
    handleSelect(value) {
      if (this.currentSelection.code !== value.code) {
        this.activeFilter = value.type || 'region'
        this.currentSelection = value
        this.emitApply()
        this.selectLocation(value)
      }
    },
    isSelected(value) {
      return this.filter.region === value.code
    },
    getLocation() {
      if (navigator.geolocation) {
        this.isLoadingCoords = true
        navigator.geolocation.getCurrentPosition(
          position => {
            this.isLoadingCoords = false
            const { latitude, longitude } = position.coords
            this.position = { latitude, longitude, distance: this.nearbyDistance }
            this.activeFilter = 'geolocation'
            this.emitApply()
          },
          () => {
            this.isLoadingCoords = false
          }
        )
      }
    }
  }
}
</script>

<style lang="scss">
$body-padding-x: 4rem;
#offers-location-modal {
  margin: 0;
  margin-top: 5px;
}

.no-results-msg {
  padding: 1rem 0;
}

.highlighted-text {
  color: color('primary');
}

ul.location-results--search {
  padding: 1rem 4rem;
  list-style: none !important;
  li {
    margin-left: 0 !important;
  }

  li button {
    padding: 1rem 0;
  }

  li.hidden {
    display: none;
  }

  & li:not(:last-child) {
    border-bottom: 1px solid color('separation-line');
  }
}

.location {
  &--header {
    color: color('dark-grey');
    padding: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    border-bottom: 1px solid color('dark-grey');

    h2 {
      margin-bottom: 0;
      font-size: 2rem;
      font-weight: 600;
    }
  }

  &--body {
    overflow-y: scroll;
    width: 100%;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &--cta {
    display: flex;
    width: 100%;
    padding: 1.5rem 3rem;
    gap: 1.5rem;
    background-color: color('grey-soft');

    @media (min-width: media('tablet-l')) {
      flex-direction: column;
    }

    .btn {
      flex: 1;
      border-radius: 100rem;
      border: none;
      margin: 0;
      padding: 1.5rem 2rem;
    }
  }
}

.location-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: none;
  width: 100%;
  overflow: hidden;

  &--desktop {
    position: absolute;
    top: 100%;
    border-radius: 1rem;

    .location--body {
      max-height: 40rem;
      .filters-section {
        width: 100%;
        top: 0;
        background: white;
        position: sticky;
        z-index: zindex('z-1');
        border-bottom: 1px solid #e1e3e3;
        .btn-search-nearby {
          padding: 2rem $body-padding-x;
          border-radius: 0;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          i {
            color: color('primary');
          }
          button:focus {
            box-shadow: none !important;
          }
        }
      }
      .location-results {
        padding: 2rem $body-padding-x;
        .title {
          color: #7e8083;
        }
        .results {
          padding: 2rem 0 0 0;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          button:focus {
            box-shadow: none;
          }
          .item {
            width: 100%;
            padding: 1rem 2rem;
            border-radius: 100rem;
            color: color('grey-cool-two');
            border: 1px solid #e1e3e3;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-left: 0;
            i {
              color: color('primary');
            }
          }
          .item-selected {
            color: color('dark-grey');
            border-color: color('dark-grey');
          }
          span {
            color: color('dark-grey');
          }
        }
      }
    }
  }

  &--mobile {
    position: fixed;
    top: 0;
    border-radius: 0;
    height: 100%;
    margin: 0 !important;

    .location--body {
      height: 100%;
      .filters-section {
        width: 100%;
        top: 0;
        background: white;
        position: sticky;
        z-index: zindex('z-1');
        padding: 2rem 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        .input-location-mobile {
          background-image: none;
          background-color: transparent;
          -webkit-box-shadow: none;
          -moz-box-shadow: none;
          box-shadow: none;
          width: 90%;
          padding: 1.25rem 2rem;
          border: 1px solid #e1e3e3;
          border-radius: 100rem;
          caret-color: color('primary');
        }
        .input-location-mobile:focus {
          outline: none;
        }
        .btn-search-nearby {
          border: 1px solid #e1e3e3;
          border-radius: 1rem;
          width: 80%;
          display: flex;
          padding: 2rem;
          justify-content: space-between;
          align-items: center;
          i {
            color: color('primary');
          }
          button:focus {
            box-shadow: none !important;
          }
        }
      }
      .location-results {
        padding: 2rem $body-padding-x;
        .title {
          color: #7e8083;
        }
        .results {
          padding: 2rem 0 0 0;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          button:focus {
            box-shadow: none;
          }
          .item {
            width: 100%;
            padding: 1rem 2rem;
            border-radius: 100rem;
            color: color('grey-cool-two');
            border: 1px solid #e1e3e3;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-left: 0;
            i {
              color: color('primary');
            }
          }
          .item-selected {
            color: color('dark-grey');
            border-color: color('dark-grey');
          }
          span {
            color: color('dark-grey');
          }
        }
      }
    }
  }
}
</style>
